import { CSSProperties, ClipboardEvent, ComponentType, useMemo } from 'react';
import styled from 'styled-components';
import {
  Checkbox as AqCheckbox,
  Field,
  GroupFieldLayout,
  NumberInput,
  RadioGroup as AqRadioGroup,
  RadioGroupItem,
  RadioGroupValue,
  TextInput,
} from '@ardoq/forms';
import { DateOnlyPicker, DateTimePicker } from '@ardoq/date-time-picker';
import {
  isDateStringValid,
  parseLocaleDateString,
  formatDateShort,
} from '@ardoq/date-time';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import { useStream } from '@ardoq/rxbeach';
import { locale$ } from 'streams/locale$';
import {
  CreatableMultiselect,
  Multiselect,
  SortableMultiselect,
} from '@ardoq/select';
import { List } from 'scopeData/editors/List';
import { User } from 'scopeData/editors/user/User';
import { FileSelector } from 'scopeData/editors/file/FileSelector';
import {
  ColorPickerProps,
  CreatableMultiListEditorProps,
  DateRangeEditorProps,
  DateRangeValidationError,
  ListEditorProps,
  MetaEditorProps,
  MultiListEditorProps,
  RemovableMetaEditorProps,
  SingleValueEditorProps,
  TextAreaEditorProps,
  RadioGroupEditorProps,
} from 'scopeData/editors/types';
import { FieldType } from '@ardoq/renderers';
import { ColorPickerInput } from '@ardoq/color-picker';
import { TertiaryButton } from '@ardoq/button';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import {
  ENTER_TAG_NAME_MESSAGE,
  INVALID_TAG_NAME_MESSAGE,
  REMOVE_FIELD_MESSAGE,
} from 'models/consts';
import { ExternallyManagedIndicatorWithPopover } from 'externallyManaged/ExternallyManagedIndicatorWithPopover';
import { DebouncedInput } from './DebouncedInput';
import { IconSelector } from './IconSelector';
import { ImageSelector } from './ImageSelector';
import { ShapeSelector } from './ShapeSelector';
import { FlexBox } from '@ardoq/layout';
import { getPastedOptions } from './getPastedOptions';
import { APIEntityType } from '@ardoq/api-types';
import { featureOperations, Features } from '@ardoq/features';
import {
  TransformationsEditor,
  TransformationsEditorProps,
} from './calculatedFields/TransformationsEditor';
import {
  buildRichTextEditorLabels,
  RichTextEditor,
} from '@ardoq/rich-text-editor';
import { useGenerateComponentDescription } from '@ardoq/component-description-editor';
import {
  DESCRIPTION_EDITOR_MIN_HEIGHT,
  richTextEditorConfig,
} from './richTextEditorUtils';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { MarkdownWithImagePreview } from '@ardoq/image-preview';
import { TripleWarning } from '@ardoq/metamodel-constraints';
import { getTripleWarningLabels } from 'locales/getMetamodelConstraintsLabels';
import type { TripleWarningData } from '../../appModelStateEdit/propertiesEditor/referencePropertiesEditor/utils';
import { ScopeDataEventLocations } from 'scopeData/tracking/types';
import { getComponentDescriptionTracking } from './componentDescriptionTracking';
import { getFeatureContext } from 'streams/featureContext$';
import { toLinkProps } from 'router/declarative/toLinkProps';
import { isValidTagName } from '@ardoq/validation';

const RichTextEditorWrapper = styled(Field)`
  .tui-editor {
    overflow-wrap: anywhere;
  }
  .tui-toolbar-icons {
    &.tui-image-custom-icon {
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="%2337475A"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"/></svg>');
      background-repeat: no-repeat;
      background-position-x: 100%;
      background-size: auto;
    }
  }
`;
const METAMODEL_CONSTRAINTS_LINK_PROPS = toLinkProps({
  route: '/organization-metamodel/constraints',
  params: {},
});

const Text = ({
  label,
  value,
  isDirty,
  errorMessages,
  description,
  isReadOnly,
  isDisabled,
  disabledMessage,
  dataClickId,
  onKeyDown,
  onValueChange,
  onBlur,
  popoverHelpContent,
  onCloseFieldClick,
  closeFieldIconTooltip,
  defaultValue,
  indicator,
}: SingleValueEditorProps) => (
  <TextInput
    defaultValue={defaultValue as string | number} // we know that Text component is used only for text of number fields
    label={label}
    value={(value as string) || undefined}
    errorMessage={isDirty ? (errorMessages?.[0] as string) : undefined}
    helperText={isDisabled ? disabledMessage : description}
    isReadOnly={isReadOnly}
    isDisabled={isDisabled}
    onValueChange={onValueChange}
    onBlur={onBlur}
    indicator={indicator}
    popoverHelpContent={popoverHelpContent}
    data-click-id={dataClickId}
    onKeyDown={onKeyDown}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
  />
);

const DebouncedText = DebouncedInput(
  Text as ComponentType<Omit<SingleValueEditorProps, 'value'>> // DebouncedInput does not pass down value prop, but relies on defaultValue prop instead
);

const DefaultValueInfo = ({ i18nLocale }: WithI18nLocale) => {
  return (
    <>
      <p>
        {t(
          i18nLocale,
          'scopeData:editors.defaultValueInfo.appliesOnlyToNewLine',
          'Default value applies only to components and references added after the field type is created.'
        )}
      </p>
      <p>
        {t(
          i18nLocale,
          'scopeData:editors.defaultValueInfo.preexistingHowTo',
          'To set preexisting components or references to the default value, right click the cell in the grid editor and set it in the context menu.'
        )}
      </p>
    </>
  );
};

const TextArea = ({
  label,
  value,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  onCloseFieldClick,
  closeFieldIconTooltip,
  toolbarExtensions,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  workspaceId,
}: TextAreaEditorProps) => {
  const richTextEditorLabels = useMemo(
    () => buildRichTextEditorLabels(i18nLocale),
    [i18nLocale]
  );
  return (
    <RichTextEditorWrapper
      label={label}
      helperText={isDisabled ? disabledMessage : description}
      isDisabled={isDisabled}
      popoverHelpContent={popoverHelpContent}
      data-click-id={dataClickId}
      onCloseFieldClick={onCloseFieldClick}
      closeFieldIconTooltip={closeFieldIconTooltip}
    >
      {isDisabled ? (
        <MarkdownWithImagePreview
          content={value as string}
          sanitationLevel="permissive"
          closeButtonText={t(
            i18nLocale,
            'scopeData:editors.imagePreview.close',
            'Close'
          )}
          zoomButtonLabel={t(
            i18nLocale,
            'scopeData:editors.imagePreview.zoom',
            'Enlarge image'
          )}
        />
      ) : (
        <RichTextEditor
          content={value as string}
          onInputChange={onValueChange}
          toolbarExtensions={toolbarExtensions}
          workspaceId={workspaceId}
          labels={richTextEditorLabels}
        />
      )}
    </RichTextEditorWrapper>
  );
};

const Number = ({
  label,
  value,
  errorMessages,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  onCloseFieldClick,
  closeFieldIconTooltip,
  displayValue,
}: SingleValueEditorProps) => (
  <NumberInput
    label={label}
    value={(value as number) ?? undefined}
    displayValue={displayValue}
    errorMessage={errorMessages?.[0] as string}
    helperText={isDisabled ? disabledMessage : description}
    isDisabled={isDisabled}
    onValueChange={onValueChange}
    allowDecimals={true}
    showUpDownArrows={!isDisabled}
    popoverHelpContent={popoverHelpContent}
    data-click-id={dataClickId}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
  />
);

const Checkbox = ({
  label,
  value,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  onCloseFieldClick,
  closeFieldIconTooltip,
  isMixed,
}: SingleValueEditorProps) => (
  <AqCheckbox
    label={label}
    isChecked={!!value}
    isMixed={isMixed}
    isDisabled={isDisabled}
    onChange={() => onValueChange(!value)}
    helperText={isDisabled ? disabledMessage : description}
    data-click-id={dataClickId}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
  >
    {label}
  </AqCheckbox>
);

const MultipleList = ({
  label,
  value,
  description,
  dataClickId,
  options,
  onValueChange,
  isDisabled,
  disabledMessage,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: MultiListEditorProps) => {
  return (
    <Multiselect
      label={label}
      value={value}
      options={options}
      onValueChange={onValueChange}
      isDisabled={isDisabled}
      helperText={isDisabled ? disabledMessage : description}
      placeholder={t(
        i18nLocale,
        'scopeData:editors.selectOptionsPlaceholder',
        'Select options...'
      )}
      closeMenuOnSelect={false}
      backspaceRemovesValue
      clickId={dataClickId}
      onCloseFieldClick={onCloseFieldClick}
      closeFieldIconTooltip={closeFieldIconTooltip}
    />
  );
};

const CreatableMultipleList = ({
  label,
  value,
  creationValidator,
  invalidOptionMessage,
  description,
  dataClickId,
  options,
  onValueChange,
  isDisabled,
  disabledMessage,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: CreatableMultiListEditorProps) => {
  return (
    <Field
      onPaste={(event: ClipboardEvent) => {
        event.preventDefault();
        const existingOptions = value ?? [];
        const pastedText = event.clipboardData.getData('text/plain');
        if (!pastedText) {
          return;
        }
        const pastedOptions = getPastedOptions(pastedText, creationValidator);
        if (!pastedOptions.length) {
          return;
        }
        onValueChange([...existingOptions, ...pastedOptions]);
      }}
    >
      <SortableMultiselect
        label={label}
        value={value}
        options={options}
        onValueChange={onValueChange}
        onChange={onValueChange}
        // @ts-expect-error issue in @ardoq/select
        isValidNewOption={creationValidator}
        noOptionsMessage={() => invalidOptionMessage}
        isDisabled={isDisabled}
        helperText={isDisabled ? disabledMessage : description}
        placeholder={t(
          i18nLocale,
          'scopeData:editors.selectOptionsPlaceholder',
          'Select options...'
        )}
        closeMenuOnSelect={false}
        backspaceRemovesValue
        clickId={dataClickId}
        onCloseFieldClick={onCloseFieldClick}
        closeFieldIconTooltip={closeFieldIconTooltip}
        isCreatable
      />
    </Field>
  );
};

const DateOnly = ({
  label,
  value,
  errorMessages,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  config,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale,
  onCalendarSelect,
  onModifiedChange,
}: SingleValueEditorProps) => (
  <Field
    label={label}
    helperText={isDisabled ? disabledMessage : description}
    isDisabled={isDisabled}
    popoverHelpContent={popoverHelpContent}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
  >
    <DateOnlyPicker
      date={parseLocaleDateString(
        value as string,
        config?.locale ?? ARDOQ_DEFAULT_LOCALE
      )}
      config={config}
      isDisabled={isDisabled}
      onChange={newDate => {
        const isValid =
          !newDate ||
          isDateStringValid(newDate, config?.locale ?? ARDOQ_DEFAULT_LOCALE);
        if (isValid) {
          onValueChange(newDate);
        }
      }}
      onCalendarSelect={onCalendarSelect}
      onModifiedChange={onModifiedChange}
      errorMessage={errorMessages?.[0] as string}
      data-click-id={dataClickId}
      i18nLocale={i18nLocale}
    />
  </Field>
);

const DateTime = ({
  label,
  value,
  errorMessages,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  config,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale,
  onCalendarSelect,
  onModifiedChange,
}: SingleValueEditorProps) => (
  <Field
    label={label}
    helperText={isDisabled ? disabledMessage : description}
    isDisabled={isDisabled}
    popoverHelpContent={popoverHelpContent}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
  >
    <DateTimePicker
      date={parseLocaleDateString(
        value as string,
        config?.locale ?? ARDOQ_DEFAULT_LOCALE
      )}
      config={config}
      isDisabled={isDisabled}
      onChange={newDate => {
        const newValue = newDate ? newDate.toISOString() : null;
        const isValid =
          !newValue ||
          isDateStringValid(newValue, config?.locale ?? ARDOQ_DEFAULT_LOCALE);
        if (isValid) {
          onValueChange(newValue);
        }
      }}
      onCalendarSelect={onCalendarSelect}
      onModifiedChange={onModifiedChange}
      errorMessage={errorMessages?.[0] as string}
      data-click-id={dataClickId}
      i18nLocale={i18nLocale}
    />
  </Field>
);

const DateOnlyRange = ({
  label,
  value,
  errorMessages,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  config,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale,
}: DateRangeEditorProps) => {
  const dateOnlyRange = value ? value : { start: null, end: null };
  const [errorMessage] = errorMessages ?? [];
  return (
    <Field
      label={label}
      helperText={isDisabled ? disabledMessage : description}
      isDisabled={isDisabled}
      popoverHelpContent={popoverHelpContent}
      errorMessage={typeof errorMessage === 'string' ? errorMessage : undefined}
      onCloseFieldClick={onCloseFieldClick}
      closeFieldIconTooltip={closeFieldIconTooltip}
    >
      <FlexBox align="flex-start" gap="medium">
        <DateOnlyPicker
          date={
            dateOnlyRange.start
              ? parseLocaleDateString(
                  dateOnlyRange.start,
                  config?.locale ?? ARDOQ_DEFAULT_LOCALE
                )
              : null
          }
          config={config}
          isDisabled={isDisabled}
          onChange={newDate => {
            const isValid =
              !newDate ||
              isDateStringValid(
                newDate,
                config?.locale ?? ARDOQ_DEFAULT_LOCALE
              );
            if (isValid) {
              onValueChange({
                ...dateOnlyRange,
                start: newDate,
              });
            }
          }}
          errorMessage={
            (errorMessage as DateRangeValidationError)?.start as string
          }
          data-click-id={`${dataClickId}-start-date`}
          i18nLocale={i18nLocale}
        />
        <DateOnlyPicker
          date={
            dateOnlyRange.end
              ? parseLocaleDateString(
                  dateOnlyRange.end,
                  config?.locale ?? ARDOQ_DEFAULT_LOCALE
                )
              : null
          }
          config={config}
          isDisabled={isDisabled}
          onChange={newDate => {
            const isValid =
              !newDate ||
              isDateStringValid(
                newDate,
                config?.locale ?? ARDOQ_DEFAULT_LOCALE
              );
            if (isValid) {
              onValueChange({
                ...dateOnlyRange,
                end: newDate,
              });
            }
          }}
          errorMessage={
            (errorMessage as DateRangeValidationError)?.end as string
          }
          data-click-id={`${dataClickId}-end-date`}
          i18nLocale={i18nLocale}
        />
      </FlexBox>
    </Field>
  );
};

const DateTimeRange = ({
  label,
  value,
  errorMessages,
  description,
  isDisabled,
  disabledMessage,
  dataClickId,
  onValueChange,
  popoverHelpContent,
  config,
  onCloseFieldClick,
  closeFieldIconTooltip,
  i18nLocale,
}: DateRangeEditorProps) => {
  const dateTimeRange = value ? value : { start: null, end: null };
  const [errorMessage] = errorMessages ?? [];
  return (
    <Field
      label={label}
      helperText={isDisabled ? disabledMessage : description}
      isDisabled={isDisabled}
      popoverHelpContent={popoverHelpContent}
      errorMessage={typeof errorMessage === 'string' ? errorMessage : undefined}
      onCloseFieldClick={onCloseFieldClick}
      closeFieldIconTooltip={closeFieldIconTooltip}
    >
      <FlexBox align="flex-start" gap="medium">
        <DateTimePicker
          date={
            dateTimeRange.start
              ? parseLocaleDateString(
                  dateTimeRange.start,
                  config?.locale ?? ARDOQ_DEFAULT_LOCALE
                )
              : null
          }
          config={config}
          isDisabled={isDisabled}
          onChange={newDate => {
            const start = newDate ? newDate.toISOString() : null;
            const isValid =
              !start ||
              isDateStringValid(start, config?.locale ?? ARDOQ_DEFAULT_LOCALE);
            if (isValid) {
              onValueChange({
                ...dateTimeRange,
                start,
              });
            }
          }}
          errorMessage={
            (errorMessage as DateRangeValidationError)?.start as string
          }
          data-click-id={`${dataClickId}-start-date`}
          i18nLocale={i18nLocale}
        />
        <DateTimePicker
          date={
            dateTimeRange.end
              ? parseLocaleDateString(
                  dateTimeRange.end,
                  config?.locale ?? ARDOQ_DEFAULT_LOCALE
                )
              : null
          }
          config={config}
          isDisabled={isDisabled}
          onChange={newDate => {
            const end = newDate ? newDate.toISOString() : null;
            const isValid =
              !end ||
              isDateStringValid(end, config?.locale ?? ARDOQ_DEFAULT_LOCALE);
            if (isValid) {
              onValueChange({
                ...dateTimeRange,
                end,
              });
            }
          }}
          errorMessage={
            (errorMessage as DateRangeValidationError)?.end as string
          }
          data-click-id={`${dataClickId}-end-date`}
          i18nLocale={i18nLocale}
        />
      </FlexBox>
    </Field>
  );
};

const RadioGroup = ({
  label,
  value,
  dataClickId,
  groupFieldLayout,
  options,
  onValueChange,
  popoverHelpContent,
  isReadOnly,
}: RadioGroupEditorProps) => {
  return (
    <AqRadioGroup
      label={label}
      value={value as RadioGroupValue}
      onValueChange={isReadOnly ? undefined : onValueChange}
      groupFieldLayout={groupFieldLayout}
      popoverHelpContent={popoverHelpContent}
    >
      {options.map(({ value: optionValue, label: optionLabel }, index) => (
        <RadioGroupItem
          key={index}
          isDisabled={isReadOnly && value !== optionValue}
          value={optionValue as RadioGroupValue}
          data-click-id={`${dataClickId}-${optionValue}`}
        >
          {optionLabel}
        </RadioGroupItem>
      ))}
    </AqRadioGroup>
  );
};

const getColorPickerLabels = (i18nLocale: I18nLocale) => ({
  placeholder: t(i18nLocale, 'colorPicker:placeholder', 'Select color'),
  resetButton: t(i18nLocale, 'colorPicker:resetButton', 'Reset'),
  customButton: t(i18nLocale, 'colorPicker:customButton', 'Custom'),
  getColorAriaLabel: (hexColor: string) =>
    t(
      i18nLocale,
      'colorPicker:colorAriaLabel',
      { color: hexColor },
      'Color {{color}}'
    ),
});

const ColorPickerWithoutReset = ({
  label,
  value,
  errorMessages,
  isDisabled,
  dataClickId,
  onValueChange,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ColorPickerProps) => {
  return (
    <Field label={label}>
      <ColorPickerInput
        value={value as string}
        isDisabled={isDisabled}
        onValueChange={onValueChange}
        errorMessage={errorMessages?.[0] as string}
        data-click-id={dataClickId}
        labels={getColorPickerLabels(i18nLocale)}
      />
    </Field>
  );
};

const ColorPickerWithReset = ({
  label,
  value,
  defaultValue,
  errorMessages,
  isDisabled,
  dataClickId,
  onValueChange,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ColorPickerProps) => {
  return (
    <Field label={label}>
      <ColorPickerInput
        value={(value || defaultValue) as string}
        isDisabled={isDisabled}
        onValueChange={onValueChange}
        errorMessage={errorMessages?.[0] as string}
        data-click-id={dataClickId}
        labels={getColorPickerLabels(i18nLocale)}
      />
      <TertiaryButton
        onClick={() => onValueChange(null)}
        dataTestId="reset-default-color"
        isDisabled={value === null || value === defaultValue}
      >
        {t(i18nLocale, 'scopeData:editors.resetToDefault', 'Reset to default')}
      </TertiaryButton>
    </Field>
  );
};

const ColorPicker = (props: ColorPickerProps) => {
  const { hasResetButton, ...rest } = props;
  if (hasResetButton) {
    return <ColorPickerWithReset {...rest} />;
  }
  return <ColorPickerWithoutReset {...rest} />;
};

export const TagEditor = ({
  label,
  value,
  options,
  isDisabled,
  onValueChange,
  autoFocus = false,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: CreatableMultiListEditorProps & { autoFocus?: boolean }) => {
  return (
    <CreatableMultiselect
      isDisabled={isDisabled}
      label={label}
      value={value}
      options={options}
      onValueChange={onValueChange}
      placeholder={t(
        i18nLocale,
        'scopeData:editors.selectTagsPlaceholder',
        'Select tags...'
      )}
      closeMenuOnSelect={false}
      backspaceRemovesValue
      isClearable={false}
      // @ts-expect-error issue in @ardoq/select
      isValidNewOption={isValidTagName}
      formatCreateLabel={(label: string) =>
        t(
          i18nLocale,
          'scopeData:editors.createTag',
          { label },
          "Create tag '{{label}}'"
        )
      }
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? t(
              i18nLocale,
              'scopeData:editors.invalidTagNameMessage',
              INVALID_TAG_NAME_MESSAGE
            )
          : t(
              i18nLocale,
              'scopeData:editors.enterTagNameMessage',
              ENTER_TAG_NAME_MESSAGE
            )
      }
      clickId="editor-input-tags"
      autoFocus={autoFocus}
    />
  );
};
type ComponentDescriptionProps = TextAreaEditorProps & {
  workspaceName: string;
};

/**
 * The sidebar form has no "edit description" gesture — it is mounted for
 * whichever component is selected — so generation stays behind an explicit
 * button here rather than running on mount the way the component overview and
 * the inventory grid do.
 */
const ComponentDescription = ({
  value,
  onValueChange,
  componentName,
  componentTypeName,
  workspaceName,
  toolbarExtensions,
  label,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  workspaceId,
}: ComponentDescriptionProps) => {
  const richTextEditorLabels = useMemo(
    () => buildRichTextEditorLabels(i18nLocale),
    [i18nLocale]
  );
  const {
    generateDescription,
    isLoading,
    hasError,
    errorMessage,
    warningMessage,
    isMissingItemToDescribe,
    labels,
  } = useGenerateComponentDescription({
    component: {
      name: componentName ?? '',
      typeName: componentTypeName ?? '',
      workspaceName,
    },
    currentValue: (value as string) ?? '',
    i18nLocale,
    tracking: getComponentDescriptionTracking(
      ScopeDataEventLocations.COMPONENT_BUILDER
    ),
    onGenerated: onValueChange,
  });

  return (
    <RichTextEditor
      content={(value as string) ?? ''}
      label={label}
      onInputChange={onValueChange}
      minHeight={DESCRIPTION_EDITOR_MIN_HEIGHT}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      toolbarExtensions={toolbarExtensions}
      workspaceId={workspaceId}
      generateDescriptionProps={{
        onClick: generateDescription,
        isLoading,
        hasError,
        isMissingItemToDescribe,
        missingItemTooltip: labels.missingItemTooltip,
        infoTooltip: labels.generateDescriptionTooltip,
        label: labels.generateLabel,
      }}
      labels={richTextEditorLabels}
    />
  );
};

const Editor = (props: MetaEditorProps) => {
  const { type, isReadOnly, locale = ARDOQ_DEFAULT_LOCALE, ...rest } = props;
  const i18nLocale = rest.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;
  const defaultValueInfo = <DefaultValueInfo i18nLocale={i18nLocale} />;

  if (isReadOnly) {
    switch (type) {
      // when FieldType.POPULATE_METHOD is readOnly, don't render anything,
      // as it is shown as suffix for the field type
      case FieldType.POPULATE_METHOD:
        return null;
      default:
        return <Text {...(rest as SingleValueEditorProps)} isReadOnly />;
    }
  }

  switch (type) {
    case FieldType.NAME:
    case FieldType.TEXT:
    case FieldType.URL:
    case FieldType.EMAIL:
    case FieldType.DISPLAY_TEXT:
    case FieldType.LABEL:
    case FieldType.FIELD_DESCRIPTION:
      return <DebouncedText {...(rest as SingleValueEditorProps)} />;
    case FieldType.DEFAULT_VALUE_TEXT:
    case FieldType.DEFAULT_VALUE_URL:
    case FieldType.DEFAULT_VALUE_EMAIL:
      return (
        <DebouncedText
          {...(rest as SingleValueEditorProps)}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.TYPE_ID_COMPONENT:
    case FieldType.PARENT:
    case FieldType.TYPE_ID_REFERENCE:
    case FieldType.SOURCE_CARDINALITY:
    case FieldType.TARGET_CARDINALITY:
    case FieldType.SOURCE:
    case FieldType.TARGET:
    case FieldType.REFERENCE_TYPE_LINE:
    case FieldType.REFERENCE_TYPE_LINE_BEGINNING:
    case FieldType.REFERENCE_TYPE_LINE_ENDING:
    case FieldType.FIELD_TYPE:
    case FieldType.NUMBER_FORMAT_OPTIONS:
      return <List {...(rest as ListEditorProps)} />;
    case FieldType.ICON:
      return <IconSelector {...(rest as SingleValueEditorProps)} />;
    case FieldType.IMAGE:
      return <ImageSelector {...(rest as ListEditorProps)} />;
    case FieldType.SHAPE:
      return <ShapeSelector {...(rest as SingleValueEditorProps)} />;
    case FieldType.LIST:
    case FieldType.START_VIEW:
    case FieldType.DEFAULT_PERSPECTIVE:
    case FieldType.DEFAULT_SORT:
      return <List {...(rest as ListEditorProps)} isClearable />;
    case FieldType.DESCRIPTION:
      return rest.entityType === APIEntityType.COMPONENT &&
        !rest.isDisabled &&
        featureOperations.hasFeature(
          getFeatureContext(),
          Features.AUTOCOMPLETE_OPENAI
        ) ? (
        <ComponentDescription
          {...(rest as ComponentDescriptionProps)}
          toolbarExtensions={richTextEditorConfig}
        />
      ) : (
        <TextArea
          {...(rest as TextAreaEditorProps)}
          toolbarExtensions={richTextEditorConfig}
        />
      );
    case FieldType.TEXT_AREA:
      return (
        <TextArea
          {...(rest as TextAreaEditorProps)}
          toolbarExtensions={richTextEditorConfig}
        />
      );
    case FieldType.DEFAULT_VALUE_TEXT_AREA:
      return (
        <TextArea
          {...(rest as TextAreaEditorProps)}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.NUMBER:
      // case FieldType.ORDER: // reference order is deprecated
      return <Number {...(rest as SingleValueEditorProps)} />;
    case FieldType.DEFAULT_VALUE_NUMBER:
      return (
        <Number
          {...(rest as SingleValueEditorProps)}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.CHECKBOX:
    case FieldType.GLOBAL:
    case FieldType.GLOBALREF:
    case FieldType.RETURNS_VALUE:
    case FieldType.HAS_CARDINALITY:
      return <Checkbox {...(rest as SingleValueEditorProps)} />;
    case FieldType.SELECT_MULTIPLE_LIST:
    case FieldType.COMPONENT_TYPE:
    case FieldType.REFERENCE_TYPE:
      return <MultipleList {...(rest as MultiListEditorProps)} />;
    case FieldType.DATE_ONLY:
      return (
        <DateOnly
          {...(rest as SingleValueEditorProps)}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DATE_TIME:
      return (
        <DateTime
          {...(rest as SingleValueEditorProps)}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DEFAULT_VALUE_DATE_ONLY:
      return (
        <DateOnly
          {...(rest as SingleValueEditorProps)}
          popoverHelpContent={defaultValueInfo}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DEFAULT_VALUE_DATE_TIME:
      return (
        <DateTime
          {...(rest as SingleValueEditorProps)}
          popoverHelpContent={defaultValueInfo}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DATE_ONLY_RANGE:
      return (
        <DateOnlyRange
          {...(rest as DateRangeEditorProps)}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DATE_TIME_RANGE:
      return (
        <DateTimeRange
          {...(rest as DateRangeEditorProps)}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DEFAULT_VALUE_DATE_ONLY_RANGE:
      return (
        <DateOnlyRange
          {...(rest as DateRangeEditorProps)}
          popoverHelpContent={defaultValueInfo}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.DEFAULT_VALUE_DATE_TIME_RANGE:
      return (
        <DateTimeRange
          {...(rest as DateRangeEditorProps)}
          popoverHelpContent={defaultValueInfo}
          config={{
            locale,
            formatter: formatDateShort,
          }}
        />
      );
    case FieldType.USER:
      return <User {...(rest as ListEditorProps)} />;
    case FieldType.DEFAULT_VALUE_USER:
      return (
        <User
          {...(rest as ListEditorProps)}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.FILE:
      return <FileSelector {...(rest as ListEditorProps)} />;
    case FieldType.DEFAULT_VALUE_FILE:
      return (
        <FileSelector
          {...(rest as ListEditorProps)}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.DEFAULT_VALUE_LIST:
    case FieldType.DEFAULT_VALUE_SELECT_MULTIPLE_LIST:
      return (
        <CreatableMultipleList {...(rest as CreatableMultiListEditorProps)} />
      );
    case FieldType.DEFAULT_VALUE_CHECKBOX:
      return (
        <RadioGroup
          {...(rest as RadioGroupEditorProps)}
          groupFieldLayout={GroupFieldLayout.HORIZONTAL}
          popoverHelpContent={defaultValueInfo}
        />
      );
    case FieldType.COLOR:
      return <ColorPicker {...(rest as ColorPickerProps)} />;
    case FieldType.TRANSFORM_NUMBER:
    case FieldType.TRANSFORM_TEXT:
    case FieldType.TRANSFORM_LIST:
      return (
        <TransformationsEditor {...(props as TransformationsEditorProps)} />
      );
    case FieldType.POPULATE_METHOD:
      return (
        <RadioGroup
          {...(rest as RadioGroupEditorProps)}
          label={undefined}
          groupFieldLayout={GroupFieldLayout.VERTICAL}
        />
      );
    case FieldType.TRIPLES_WARNING:
      return (
        <TripleWarning
          {...(rest.value as TripleWarningData)}
          canManageMetamodelConstraints={
            rest.canManageMetamodelConstraints ?? false
          }
          manageConstraintsLinkProps={METAMODEL_CONSTRAINTS_LINK_PROPS}
          labels={getTripleWarningLabels(
            rest.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE
          )}
        />
      );
    default:
      return null;
  }
};

const topRightContentStyles: CSSProperties = {
  color: 'inherit',
  padding: 0,
  position: 'relative',
  float: 'right',
  right: 0,
  top: 0,
};

const EditorEnabledWithManagedIndicator = (props: MetaEditorProps) => {
  const { isExternallyManaged, externallyManagedWorkspaceId, ...rest } = props;
  if (!isExternallyManaged) {
    return <Editor {...rest} />;
  }
  return (
    <span>
      <span style={topRightContentStyles}>
        <ExternallyManagedIndicatorWithPopover
          workspaceId={externallyManagedWorkspaceId!}
          i18nLocale={props.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE}
        />
      </span>
      <Editor {...rest} />
    </span>
  );
};

const RemovableEditor = (props: RemovableMetaEditorProps) => {
  const { isRemovable, onRemove, ...rest } = props;
  if (!isRemovable) {
    return <EditorEnabledWithManagedIndicator {...rest} />;
  }
  return (
    <EditorEnabledWithManagedIndicator
      onCloseFieldClick={onRemove}
      closeFieldIconTooltip={REMOVE_FIELD_MESSAGE}
      {...rest}
    />
  );
};

// locale$ is a warm app-level stream — subscribe at this boundary and thread the
// live date-fns Locale down as a prop. A useStream wrapper (rather than connect)
// keeps RemovableMetaEditorProps intact as a discriminated union: connect's
// Omit<Props, 'locale'> collapses the union to its common keys and drops
// variant-only props like toolbarExtensions.
const ConnectedEditor = (props: RemovableMetaEditorProps) => {
  const locale = useStream(locale$, ARDOQ_DEFAULT_LOCALE);
  return <RemovableEditor {...props} locale={locale} />;
};

export { ConnectedEditor as Editor };
